import { differenceInDays, parseISO } from 'date-fns';
import { paths } from 'src/routes/paths';
import { isActiveMembership, isManagerMembership, webEligibleMemberships } from './roles';

export const isOrganizationCurrent = (org) => {
  let current = false;
  try {
    if (!org?.end_date) {
      current = true;
    } else if (new Date(org?.end_date) > new Date()) {
      current = true;
    }
  } catch (error) {}
  return current;
};

export const isOrganizationActive = (org) => {
  return org?.organizations_status?.code === 'active';
};

export const isOrganizationInternal = (org) => {
  return org?.flg_prod !== 'Y';
};

/** Every membership row belonging to this user in this org — there can be more than one. */
const userMemberships = (org, userId) =>
  (org?.organizations_user_organizations ?? []).filter(
    (membership) => membership?.user_id === userId
  );

/**
 * The membership that governs this user in this org: the very row that made the org eligible
 * (see webEligibleMemberships), so the org selector and the effective role can never disagree.
 *
 * Picking "the first active row" instead would be a bug, because a user can hold more than one
 * row per org and Hasura returns them in no particular order. webEligibleMemberships sorts
 * manager rows first precisely so that [0] here resolves to manager for such a user, rather
 * than confining them to the volunteer screens.
 *
 * The fallback to any active row only matters if an org was selected that eligibility would
 * have rejected; resolving the real (lesser) role there is correct — the guard should confine.
 */
export const findGoverningMembership = (org, globalRole, userId) => {
  const memberships = userMemberships(org, userId);
  return (
    webEligibleMemberships(globalRole, memberships)[0] ?? memberships.find(isActiveMembership)
  );
};

/**
 * The orgs an account may act on in the web app, manager orgs first — see webEligibleMemberships
 * for the eligibility rule and the reason for the ordering. auth-provider.js picks its landing
 * role from that same manager-first ordering, but over the user's flat cross-org membership
 * list; mirroring the ordering here keeps this org list in agreement with it, so a user who
 * manages org B and only volunteers in org A lands on org B instead of being routed to a
 * manager landing path while the org context selects the volunteer org.
 *
 * Anything filtered out here is invisible to the whole app, not just to the org selector.
 */
export const getEligibleOrganizations = (organizations, globalRole, userId) => {
  const eligible = (organizations ?? []).filter(
    (org) => webEligibleMemberships(globalRole, userMemberships(org, userId)).length > 0
  );
  const isManagerOrg = (org) => userMemberships(org, userId).some(isManagerMembership);
  return [...eligible.filter(isManagerOrg), ...eligible.filter((org) => !isManagerOrg(org))];
};

export const isOrganizationExpiringSoon = (org) => {
  let expiringSoon = false;
  try {
    const startDate = org?.end_date;
    const parsedDate = parseISO(startDate, 'YYYY-MM-DD');
    const diff = differenceInDays(parsedDate, new Date());
    expiringSoon = diff <= 30;
  } catch (error) {
    console.log(error);
  }

  return expiringSoon;
};

/**
 * The one corner of the app an expired or deactivated org may still reach: Billing, so the org
 * can see what it owes and renew. Both halves of the membership lock read this — the card over
 * the page content (layouts/dashboard/main) and the dimmed nav (nav-section-vertical) — so the
 * route that stays open and the menu item that stays clickable can never disagree.
 */
export const isLockExemptPath = (path) =>
  typeof path === 'string' &&
  (path === paths.billing.root || path.startsWith(`${paths.billing.root}/`));
